// src/components/ndr-management/NDRActionModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";

export default function NDRActionModal({ ndr, onClose, onSubmit, preSelectedAction }) {
  const [selectedAction, setSelectedAction] = useState(preSelectedAction || "");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null); // { success: boolean, message: string }
  const confirmationRef = useRef(null);
  const modalContentRef = useRef(null);
  
  // Get today's date in YYYY-MM-DD format for the date input
  const today = format(new Date(), "yyyy-MM-dd");
  
  const [formData, setFormData] = useState({
    // Reattempt fields
    dropPoc: ndr.delivery_address?.drop_poc || "",
    dropAddress: ndr.delivery_address?.drop_address || "",
    dropCity: ndr.delivery_address?.drop_city || "",
    dropState: ndr.delivery_address?.drop_state || "",
    dropLandmark: ndr.delivery_address?.drop_landmark || "",
    dropPocContactNumber: ndr.delivery_address?.drop_poc_contact_number || "",
    dropPostalCode: ndr.delivery_address?.drop_postal_code || "",
    dropCountryCode: "IN",
    preferredDeliveryDate: today,
    internalRemarks: "",
  });

  const availableActions = ndr.ndr_actions || [];

  useEffect(() => {
    if (preSelectedAction) {
      setSelectedAction(preSelectedAction);
    }
  }, [preSelectedAction]);

  // Auto-scroll to bottom when confirmation panel appears
  useEffect(() => {
    if (showConfirmation && modalContentRef.current) {
      setTimeout(() => {
        modalContentRef.current.scrollTo({
          top: modalContentRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [showConfirmation]);

  // Auto-scroll when result appears
  useEffect(() => {
    if (result && modalContentRef.current) {
      setTimeout(() => {
        modalContentRef.current.scrollTo({
          top: modalContentRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [result]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedAction) {
      alert("Please select an action");
      return;
    }

    // Show confirmation panel instead of alert
    setShowConfirmation(true);
  };

  // Map action names to action codes
  const getActionCode = (actionName) => {
    const actionCodeMap = {
      "REATTEMPT": "100",
      "INITIATE_RTO": "200"
    };
    return actionCodeMap[actionName] || actionName;
  };

  const handleConfirmSubmit = async () => {
    const payload = {
      merchant_id: ndr.merchant_id,
      order_reference_number: ndr.order_reference_number,
      ndr_action_type_code: getActionCode(selectedAction), // Convert to code: "100" or "200"
    };

    if (selectedAction === "REATTEMPT") {
      payload.reattempt_action_details = {
        drop_poc: formData.dropPoc,
        drop_address: formData.dropAddress,
        drop_city: formData.dropCity,
        drop_state: formData.dropState,
        drop_landmark: formData.dropLandmark || null,
        drop_poc_contact_number: formData.dropPocContactNumber,
        drop_postal_code: formData.dropPostalCode,
        drop_country_code: formData.dropCountryCode,
        preferred_delivery_date: formData.preferredDeliveryDate || null,
        internal_remarks: formData.internalRemarks || null,
      };
    } else if (selectedAction === "INITIATE_RTO") {
      payload.initiate_rto_action_details = {
        internal_remarks: formData.internalRemarks || null,
      };
    }

    // Hide confirmation and show loader
    setShowConfirmation(false);
    setIsLoading(true);

    try {
      // Call the onSubmit function and await the result
      await onSubmit(payload);
      
      // Show success message
      setResult({
        success: true,
        message: "NDR action has been successfully submitted!"
      });
      setIsLoading(false);
    } catch (error) {
      // Show error message
      setResult({
        success: false,
        message: error.message || "Failed to submit NDR action. Please try again."
      });
      setIsLoading(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleCloseResult = () => {
    if (result?.success) {
      onClose(); // Close modal on success
    } else {
      setResult(null); // Clear error and allow retry
      setShowConfirmation(false);
    }
  };

  // Get action label for display
  const getActionLabel = (action) => {
    if (action === "REATTEMPT") return "Reattempt Delivery";
    if (action === "INITIATE_RTO") return "Initiate RTO (Return to Origin)";
    return action;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        ref={modalContentRef}
        className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold">Take NDR Action</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Order Info */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold mb-2">Order Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Order Reference:</span>
                <span className="ml-2 font-medium">
                  {ndr.order_reference_number}
                </span>
              </div>
              <div>
                <span className="text-gray-600">AWB:</span>
                <span className="ml-2 font-medium">{ndr.awb_number}</span>
              </div>
              <div>
                <span className="text-gray-600">Current Status:</span>
                <span className="ml-2 font-medium">
                  {ndr.current_order_status_description_external || ndr.current_order_status}
                </span>
              </div>
              <div>
                <span className="text-gray-600">NDR Status:</span>
                <span className="ml-2 font-medium">
                  {ndr.latest_ndr_status_description || "NDR"}
                </span>
              </div>
            </div>
          </div>

          {/* Action Label (instead of dropdown when pre-selected) */}
          {preSelectedAction ? (
            <div className="mb-6">
              <div className="w-full px-4 py-3 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <span className="text-lg font-semibold text-blue-900">
                  {getActionLabel(preSelectedAction)}
                </span>
              </div>
            </div>
          ) : (
            /* Action Selection (when no pre-selection) */
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Action <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                required
                disabled={isLoading || result}
              >
                <option value="">-- Choose an action --</option>
                {availableActions.map((action, index) => (
                  <option key={index} value={action.action}>
                    {action.action}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Reattempt Form */}
          {selectedAction === "REATTEMPT" && !isLoading && !result && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-lg mb-4">
                Reattempt Delivery Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="dropPoc"
                    value={formData.dropPoc}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    required
                    disabled={showConfirmation}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="dropPocContactNumber"
                    value={formData.dropPocContactNumber}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    required
                    disabled={showConfirmation}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="dropAddress"
                    value={formData.dropAddress}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    required
                    disabled={showConfirmation}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="dropCity"
                    value={formData.dropCity}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    required
                    disabled={showConfirmation}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="dropState"
                    value={formData.dropState}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    required
                    disabled={showConfirmation}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="dropPostalCode"
                    value={formData.dropPostalCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    required
                    disabled={showConfirmation}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country Code
                  </label>
                  <input
                    type="text"
                    name="dropCountryCode"
                    value={formData.dropCountryCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-400"
                    disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Landmark
                  </label>
                  <input
                    type="text"
                    name="dropLandmark"
                    value={formData.dropLandmark}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    disabled={showConfirmation}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Delivery Date
                  </label>
                  <input
                    type="date"
                    name="preferredDeliveryDate"
                    value={formData.preferredDeliveryDate}
                    onChange={handleChange}
                    min={today}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    disabled={showConfirmation}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Internal Remarks
                  </label>
                  <textarea
                    name="internalRemarks"
                    value={formData.internalRemarks}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                    placeholder="Add any internal notes..."
                    disabled={showConfirmation}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Initiate RTO Form */}
          {selectedAction === "INITIATE_RTO" && !isLoading && !result && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-lg mb-4">
                Initiate RTO (Return to Origin)
              </h3>

              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ This will initiate a return to origin process for this shipment. The package will be sent back to the pickup address.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Remarks
                </label>
                <textarea
                  name="internalRemarks"
                  value={formData.internalRemarks}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                  placeholder="Add reason for RTO or any internal notes..."
                  disabled={showConfirmation}
                />
              </div>
            </div>
          )}

          {/* Loading Spinner */}
          {isLoading && (
            <div className="mt-6 flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
              <p className="mt-4 text-lg font-medium text-gray-700">Processing your request...</p>
              <p className="mt-2 text-sm text-gray-500">Please wait while we submit your action</p>
            </div>
          )}

          {/* Result Panel */}
          {result && (
            <div className="mt-6">
              <div 
                className={`p-6 rounded-lg border-2 ${
                  result.success 
                    ? 'bg-green-50 border-green-300' 
                    : 'bg-red-50 border-red-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {result.success ? (
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-grow">
                    <h3 className={`text-xl font-bold mb-2 ${
                      result.success ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {result.success ? 'Success!' : 'Error'}
                    </h3>
                    <p className={`text-sm mb-4 ${
                      result.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {result.message}
                    </p>
                    <button
                      type="button"
                      onClick={handleCloseResult}
                      className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                        result.success
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {result.success ? 'Close' : 'Try Again'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Panel at Bottom */}
          {showConfirmation && !isLoading && !result && (
            <div 
              ref={confirmationRef}
              className="mt-6 bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-grow">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                    Confirm Action
                  </h3>
                  <p className="text-sm text-yellow-800 mb-4">
                    Are you sure you want to <strong>{getActionLabel(selectedAction)}</strong> for order <strong>{ndr.order_reference_number}</strong>?
                    {selectedAction === "INITIATE_RTO" && (
                      <span className="block mt-2">
                        This will send the package back to the pickup address.
                      </span>
                    )}
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleConfirmSubmit}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium transition-colors"
                    >
                      Yes, Proceed
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelConfirmation}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!showConfirmation && !isLoading && !result && (
            <div className="flex gap-3 mt-6 pt-6 border-t">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
              >
                Submit Action
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}